import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "sonner";
import {
  Sparkles, Lock, LogOut, Search, Download, Loader2, Users, Mail, Star,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "kyvelra_admin_token";

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

/* ---------------- Login ---------------- */
const Login = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back.");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-navy-950 px-6 grain">
      <div className="pointer-events-none absolute left-1/2 top-1/4 h-[40vh] w-[40vh] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(47,107,255,0.25),transparent_60%)] blur-3xl" />
      <div className="glass-dark relative z-10 w-full max-w-sm rounded-3xl p-8" data-testid="admin-login-card">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-electric text-white">
            <Sparkles className="h-4 w-4" />
          </span>
          <span className="font-serif text-xl text-white">Kyvelra</span>
        </div>
        <h1 className="mt-6 font-serif text-3xl text-white">Admin access</h1>
        <p className="mt-2 font-body text-sm text-electric-ice/60">
          Sign in to view your waitlist signups.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="admin-login-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@kyvelra.com"
            data-testid="admin-email-input"
            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 font-display text-sm text-white placeholder:text-electric-ice/40 outline-none focus:border-electric"
          />
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-electric-ice/40" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="admin-password-input"
              className="w-full rounded-xl border border-white/10 bg-white/5 py-3 pl-11 pr-4 font-display text-sm text-white placeholder:text-electric-ice/40 outline-none focus:border-electric"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="btn-glow flex w-full items-center justify-center gap-2 rounded-xl bg-electric py-3 font-display font-semibold text-white transition-colors hover:bg-electric-bright disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

/* ---------------- Dashboard ---------------- */
const StatCard = ({ icon: Icon, label, value, tid }) => (
  <div className="rounded-2xl border border-navy-800 bg-navy-900 p-6" data-testid={tid}>
    <div className="flex items-center justify-between">
      <span className="eyebrow text-electric-ice/40">{label}</span>
      <Icon className="h-4 w-4 text-electric" />
    </div>
    <div className="mt-3 font-serif text-4xl text-white">{value}</div>
  </div>
);

const Badge = ({ interest }) => {
  const founding = interest === "founding";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-display text-xs font-semibold ${
        founding ? "bg-electric/15 text-electric-bright" : "bg-emerald-400/10 text-emerald-300"
      }`}
    >
      {founding ? <Star className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
      {founding ? "Founding Recruiter" : "Beta Waitlist"}
    </span>
  );
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Dashboard = ({ onLogout }) => {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: 0, beta: 0, founding: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        axios.get(`${API}/admin/waitlist`, { headers: authHeader() }),
        axios.get(`${API}/admin/stats`, { headers: authHeader() }),
      ]);
      setEntries(list.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Failed to load signups.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      const matchQ = e.email.toLowerCase().includes(query.toLowerCase());
      const matchF = filter === "all" || e.interest === filter;
      return matchQ && matchF;
    });
  }, [entries, query, filter]);

  const exportCsv = () => {
    const rows = [["Email", "List", "Signed up (ISO)"]].concat(
      filtered.map((e) => [
        e.email,
        e.interest === "founding" ? "Founding Recruiter" : "Beta Waitlist",
        e.created_at,
      ])
    );
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kyvelra-signups-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  const tabs = [
    { k: "all", label: "All" },
    { k: "beta", label: "Beta" },
    { k: "founding", label: "Founding" },
  ];

  return (
    <div className="min-h-screen bg-navy-950 text-white" data-testid="admin-dashboard">
      <header className="border-b border-navy-800 px-6 py-5">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-electric text-white">
              <Sparkles className="h-4 w-4" />
            </span>
            <div>
              <span className="font-serif text-lg">Kyvelra</span>
              <span className="ml-2 font-display text-xs text-electric-ice/40">Signups</span>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 rounded-full border border-navy-700 px-4 py-2 font-display text-sm text-electric-ice/70 transition-colors hover:border-electric hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard icon={Users} label="Total signups" value={stats.total} tid="stat-total" />
          <StatCard icon={Mail} label="Beta waitlist" value={stats.beta} tid="stat-beta" />
          <StatCard icon={Star} label="Founding recruiters" value={stats.founding} tid="stat-founding" />
        </div>

        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-electric-ice/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by email…"
              data-testid="admin-search-input"
              className="w-full rounded-full border border-navy-800 bg-navy-900 py-2.5 pl-10 pr-4 font-display text-sm text-white placeholder:text-electric-ice/40 outline-none focus:border-electric"
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-full border border-navy-800 bg-navy-900 p-1">
              {tabs.map((t) => (
                <button
                  key={t.k}
                  onClick={() => setFilter(t.k)}
                  data-testid={`filter-${t.k}`}
                  className={`rounded-full px-4 py-1.5 font-display text-sm transition-colors ${
                    filter === t.k ? "bg-electric text-white" : "text-electric-ice/60 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              onClick={exportCsv}
              data-testid="admin-export-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-display text-sm font-semibold text-navy-950 transition-transform hover:-translate-y-0.5"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-navy-800">
          <table className="w-full text-left" data-testid="admin-table">
            <thead className="bg-navy-900">
              <tr className="font-display text-xs uppercase tracking-wider text-electric-ice/40">
                <th className="px-6 py-4">Email</th>
                <th className="px-6 py-4">List</th>
                <th className="px-6 py-4 text-right">Signed up</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-800">
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-16 text-center text-electric-ice/50">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-16 text-center font-body text-electric-ice/50" data-testid="admin-empty">
                    No signups yet.
                  </td>
                </tr>
              ) : (
                filtered.map((e) => (
                  <tr key={e.id} className="transition-colors hover:bg-navy-900/60" data-testid="admin-row">
                    <td className="px-6 py-4 font-display text-sm text-white">{e.email}</td>
                    <td className="px-6 py-4"><Badge interest={e.interest} /></td>
                    <td className="px-6 py-4 text-right font-body text-sm text-electric-ice/60">{fmtDate(e.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-4 font-display text-xs text-electric-ice/40">
          Showing {filtered.length} of {entries.length} signups.
        </p>
      </main>
    </div>
  );
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return;
    axios
      .get(`${API}/admin/me`, { headers: authHeader() })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      })
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="grid min-h-screen place-items-center bg-navy-950">
        <Loader2 className="h-6 w-6 animate-spin text-electric" />
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-center" richColors />
      {authed ? <Dashboard onLogout={logout} /> : <Login onSuccess={() => setAuthed(true)} />}
    </>
  );
}
